import { useRef, useState } from 'react';
import { ApiError, api } from '../../lib/api';
import logoWhite from '../../assets/logo-white.svg';
import { Select } from '../../components/Select';
import styles from './IntakeFormPage.module.css';

const PLATFORMS = [
  'TikTok',
  'Instagram',
  'YouTube',
  'Facebook',
  'X (Twitter)',
  'LinkedIn',
  'Snapchat',
  'Other',
];

// HANDLE_ONLY_PLATFORMS mirror the server-side handleOnlyPlatforms map in
// internal/intake/validate.go. These platforms are canonically addressed by an
// @handle; everything else (YouTube, Facebook, LinkedIn, Other) wants a profile
// URL. The form renders one input or the other based on the picked platform.
const HANDLE_ONLY_PLATFORMS = new Set(['Instagram', 'X (Twitter)', 'TikTok', 'Snapchat']);
const isHandlePlatform = (p: string) => HANDLE_ONLY_PLATFORMS.has(p);

const stripAt = (s: string) => s.replace(/^@+/, '').trimStart();

type PhFieldErrs = Partial<Record<'platformName' | 'handle' | 'followerCount' | 'platformLink', string>>;

const PH_FIELD_LABELS: Record<keyof PhFieldErrs, string> = {
  platformName: 'Platform',
  handle: 'Handle',
  followerCount: 'Followers',
  platformLink: 'Link',
};

interface PhRow {
  id: number;
  platform: string;
  other: string;
  handle: string;
  followers: string;
  link: string;
  err: boolean;
  errs?: PhFieldErrs;
}

type FormErrs = Partial<
  Record<
    | 'fullName'
    | 'email'
    | 'mainLink'
    | 'contentDescription'
    | 'audienceDescription'
    | 'ideaDetail'
    | 'helpDetail'
    | 'product'
    | 'rate'
    | 'revisions'
    | 'intake'
    | 'hasIdeas'
    | 'extraNotes'
    | 'paypalEmail'
    | 'paypalRecipientName'
    | 'paypalPhone'
    | 'platformHandles',
    string
  >
>;

let phSeq = 0;
function newRow(): PhRow {
  return {
    id: ++phSeq,
    platform: '',
    other: '',
    handle: '',
    followers: '',
    link: '',
    err: false,
  };
}

export function IntakeFormPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [mainLink, setMainLink] = useState('');
  const [contentDescription, setContentDescription] = useState('');
  const [audienceDescription, setAudienceDescription] = useState('');
  const [productBeta, setProductBeta] = useState(false);
  const [productSleep, setProductSleep] = useState(false);
  const [hasIdeas, setHasIdeas] = useState<'' | 'yes' | 'no'>('');
  const [ideaDetail, setIdeaDetail] = useState('');
  const [helpDetail, setHelpDetail] = useState('');
  const [agreedRate, setAgreedRate] = useState(false);
  const [agreedRevisions, setAgreedRevisions] = useState(false);
  const [agreedIntake, setAgreedIntake] = useState(false);
  const [extraNotes, setExtraNotes] = useState('');
  const [paypalEmail, setPaypalEmail] = useState('');
  const [paypalRecipientName, setPaypalRecipientName] = useState('');
  const [paypalPhone, setPaypalPhone] = useState('');
  const [phRows, setPhRows] = useState<PhRow[]>([newRow()]);
  const emailRef = useRef<HTMLInputElement>(null);

  const [errs, setErrs] = useState<FormErrs>({});

  function setPhField<K extends keyof PhRow>(id: number, key: K, val: PhRow[K]) {
    setPhRows((rows) =>
      rows.map((r) => {
        if (r.id !== id) return r;
        const next: PhRow = { ...r, [key]: val, err: false, errs: undefined };
        // When the platform changes, clear whichever of handle/link is no longer
        // applicable so the payload only carries the field the new platform uses.
        if (key === 'platform') {
          if (isHandlePlatform(val as string)) {
            next.link = '';
          } else {
            next.handle = '';
          }
        }
        return next;
      }),
    );
  }

  function addPhRow() {
    setPhRows((rows) => [...rows, newRow()]);
  }

  function removePhRow(id: number) {
    setPhRows((rows) => (rows.length > 1 ? rows.filter((r) => r.id !== id) : rows));
  }

  function validate(): boolean {
    const e: FormErrs = {};
    if (!fullName.trim()) e.fullName = 'Required';
    if (!email.trim()) e.email = 'Required';
    if (!mainLink.trim()) e.mainLink = 'Required';
    if (!contentDescription.trim()) e.contentDescription = 'Required';
    if (!audienceDescription.trim()) e.audienceDescription = 'Required';
    if (hasIdeas === 'yes' && !ideaDetail.trim()) e.ideaDetail = 'Required';
    if (hasIdeas === 'no' && !helpDetail.trim()) e.helpDetail = 'Required';
    if (!productBeta && !productSleep) e.product = 'Select at least one product';
    if (!agreedRate) e.rate = 'Required';
    if (!agreedRevisions) e.revisions = 'Required';
    if (!agreedIntake) e.intake = 'Required';
    if (!hasIdeas) e.hasIdeas = 'Required';
    const ppEmail = paypalEmail.trim();
    if (!ppEmail) {
      e.paypalEmail = 'Required';
    } else if (!ppEmail.includes('@')) {
      e.paypalEmail = 'Must be a valid email address';
    }
    setErrs(e);

    const nextRows = phRows.map((r) => {
      const platformOK = r.platform && (r.platform !== 'Other' || r.other.trim());
      const handleOK = isHandlePlatform(r.platform) ? !!r.handle.trim() : true;
      const linkOK = !isHandlePlatform(r.platform) && r.platform ? !!r.link.trim() : true;
      return { ...r, err: !platformOK || !handleOK || !linkOK, errs: undefined };
    });
    setPhRows(nextRows);
    const phOK = nextRows.every((r) => !r.err);

    return Object.keys(e).length === 0 && phOK;
  }

  function applyServerErrors(fieldErrors: Record<string, string>) {
    const next: FormErrs = {};
    const rowKey = /^platformHandles\[(\d+)\]\.(platformName|handle|followerCount|platformLink)$/;
    const rowErrs = new Map<number, PhFieldErrs>();
    let firstFocusable: 'email' | null = null;

    for (const [key, msg] of Object.entries(fieldErrors)) {
      const m = rowKey.exec(key);
      if (m) {
        const idx = parseInt(m[1], 10);
        const field = m[2] as keyof PhFieldErrs;
        const cur = rowErrs.get(idx) ?? {};
        cur[field] = msg;
        rowErrs.set(idx, cur);
        continue;
      }
      switch (key) {
        case 'fullName':
        case 'email':
        case 'mainLink':
        case 'contentDescription':
        case 'audienceDescription':
        case 'ideaDetail':
        case 'helpDetail':
        case 'extraNotes':
        case 'hasIdeas':
        case 'paypalEmail':
        case 'paypalRecipientName':
        case 'paypalPhone':
          next[key] = msg;
          break;
        case 'productInterest':
          next.product = msg;
          break;
        case 'agreedRate':
          next.rate = msg;
          break;
        case 'agreedRevisions':
          next.revisions = msg;
          break;
        case 'agreedIntake':
          next.intake = msg;
          break;
        case 'platformHandles':
          next.platformHandles = msg;
          break;
      }
      if (key === 'email' && !firstFocusable) firstFocusable = 'email';
    }
    setErrs(next);
    setPhRows((rows) =>
      rows.map((r, i) => {
        const re = rowErrs.get(i);
        return re ? { ...r, err: true, errs: re } : { ...r, err: false, errs: undefined };
      }),
    );
    if (firstFocusable === 'email') {
      emailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      emailRef.current?.focus({ preventScroll: true });
    }
  }

  async function submit(ev: React.FormEvent) {
    ev.preventDefault();
    if (!validate()) {
      setErrMsg('Please complete all required fields before submitting.');
      return;
    }
    setErrMsg('');
    setSubmitting(true);
    try {
      const platformHandles = phRows.map((r) => {
        const platformName = r.platform === 'Other' ? r.other.trim() : r.platform;
        const handleOnly = isHandlePlatform(r.platform);
        return {
          platformName,
          handle: handleOnly ? stripAt(r.handle.trim()) : '',
          followerCount: parseInt(r.followers, 10) || 0,
          platformLink: handleOnly ? '' : r.link.trim(),
        };
      });
      const productInterest: string[] = [];
      if (productBeta) productInterest.push('Beta Blockers (situational anxiety)');
      if (productSleep) productInterest.push('Prescription Sleep Plan');
      await api.post('/api/applications', {
        fullName: fullName.trim(),
        email: email.trim(),
        mainLink: mainLink.trim(),
        contentDescription: contentDescription.trim(),
        audienceDescription: audienceDescription.trim(),
        productInterest,
        hasIdeas,
        ideaDetail: hasIdeas === 'yes' ? ideaDetail.trim() : '',
        helpDetail: hasIdeas === 'no' ? helpDetail.trim() : '',
        agreedRate,
        agreedRevisions,
        agreedIntake,
        extraNotes: extraNotes.trim(),
        paypalEmail: paypalEmail.trim(),
        paypalRecipientName: paypalRecipientName.trim(),
        paypalPhone: paypalPhone.trim(),
        platformHandles,
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setErrs((prev) => ({ ...prev, email: err.message }));
        setErrMsg(err.message);
        emailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        emailRef.current?.focus({ preventScroll: true });
        return;
      }
      if (err instanceof ApiError && err.fieldErrors) {
        applyServerErrors(err.fieldErrors);
        setErrMsg('Please correct the highlighted fields.');
        return;
      }
      const msg = err instanceof Error ? err.message : 'Something went wrong — please try again.';
      setErrMsg(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={styles.shell}>
      <div className={styles.hero}>
        <div className={styles.heroInner}>
          <img src={logoWhite} alt="Kick" className={styles.logo} />
          <h1 className={styles.heroTitle}>Creator Partnership Application</h1>
          <p className={styles.heroSub}>
            Tell us about yourself and your content. We review every application and will be in
            touch if there's a fit.
          </p>
        </div>
      </div>

      <div className={styles.formWrap}>
        {submitted ? (
          <div className={`${styles.sectionCard} ${styles.success}`}>
            <div className={styles.successIcon}>✓</div>
            <h2>Application received!</h2>
            <p>
              Thank you for your interest in working with Kick Health. We will review submissions
              and let you know next steps if we choose to move forward.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} noValidate>
            <div className={styles.sectionCard}>
              <div className={styles.sectionTitle}>About You</div>
              <div className={styles.fgrid}>
                <div className={`${styles.fitem} ${styles.fitemFull}`}>
                  <label className={styles.label}>
                    Full Name <span className={styles.req}>*</span>
                  </label>
                  <input
                    className={`${styles.input} ${errs.fullName ? styles.err : ''}`}
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    autoComplete="name"
                    placeholder="Your full name"
                  />
                  {errs.fullName && <div className={styles.errInline}>{errs.fullName}</div>}
                </div>
                <div className={`${styles.fitem} ${styles.fitemFull}`}>
                  <label className={styles.label}>
                    Email Address <span className={styles.req}>*</span>
                  </label>
                  <input
                    ref={emailRef}
                    type="email"
                    className={`${styles.input} ${errs.email ? styles.err : ''}`}
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (errs.email) setErrs((prev) => ({ ...prev, email: undefined }));
                    }}
                    autoComplete="email"
                    placeholder="you@example.com"
                  />
                  {errs.email && <div className={styles.errInline}>{errs.email}</div>}
                </div>
                <div className={`${styles.fitem} ${styles.fitemFull}`}>
                  <label className={styles.label}>
                    Your platforms and approximate follower counts{' '}
                    <span className={styles.req}>*</span>
                  </label>
                  <div className={styles.phRows}>
                    {phRows.map((r) => (
                      <div
                        key={r.id}
                        className={`${styles.phRow} ${r.platform === 'Other' ? styles.phRowOther : ''} ${r.err ? styles.phRowErr : ''}`}
                      >
                        <Select
                          className={styles.phPlatform}
                          value={r.platform}
                          onChange={(e) => setPhField(r.id, 'platform', e.target.value)}
                          placeholder="Platform…"
                        >
                          {PLATFORMS.map((p) => (
                            <option key={p} value={p}>
                              {p}
                            </option>
                          ))}
                        </Select>
                        {r.platform === 'Other' && (
                          <input
                            className={styles.phOther}
                            type="text"
                            placeholder="Platform name"
                            value={r.other}
                            onChange={(e) => setPhField(r.id, 'other', e.target.value)}
                          />
                        )}
                        {isHandlePlatform(r.platform) ? (
                          <input
                            className={styles.phHandle}
                            type="text"
                            placeholder="handle"
                            value={r.handle}
                            onChange={(e) => setPhField(r.id, 'handle', stripAt(e.target.value))}
                          />
                        ) : (
                          <input
                            className={styles.phLink}
                            type="url"
                            placeholder="https://…"
                            value={r.link}
                            onChange={(e) => setPhField(r.id, 'link', e.target.value)}
                          />
                        )}
                        <input
                          className={styles.phFollowers}
                          type="number"
                          placeholder="Followers"
                          min={0}
                          value={r.followers}
                          onChange={(e) => setPhField(r.id, 'followers', e.target.value)}
                        />
                        {phRows.length > 1 && (
                          <button
                            type="button"
                            className={styles.phRemove}
                            onClick={() => removePhRow(r.id)}
                            aria-label="Remove platform"
                          >
                            ×
                          </button>
                        )}
                        {r.errs &&
                          (Object.keys(r.errs) as (keyof PhFieldErrs)[]).map((k) =>
                            r.errs?.[k] ? (
                              <div key={k} className={styles.errInline} style={{ gridColumn: '1 / -1' }}>
                                {PH_FIELD_LABELS[k]}: {r.errs[k]}
                              </div>
                            ) : null,
                          )}
                      </div>
                    ))}
                  </div>
                  {errs.platformHandles && (
                    <div className={styles.errInline}>{errs.platformHandles}</div>
                  )}
                  <button type="button" className={styles.phAdd} onClick={addPhRow}>
                    + Add another platform
                  </button>
                </div>
                <div className={`${styles.fitem} ${styles.fitemFull}`}>
                  <label className={styles.label}>
                    Please provide a link to your main account{' '}
                    <span className={styles.req}>*</span>
                  </label>
                  <input
                    type="url"
                    className={`${styles.input} ${errs.mainLink ? styles.err : ''}`}
                    value={mainLink}
                    onChange={(e) => setMainLink(e.target.value)}
                    placeholder="https://"
                  />
                  {errs.mainLink && <div className={styles.errInline}>{errs.mainLink}</div>}
                </div>
              </div>
            </div>

            <div className={styles.sectionCard}>
              <div className={styles.sectionTitle}>Your Content</div>
              <div className={styles.fgrid}>
                <div className={`${styles.fitem} ${styles.fitemFull}`}>
                  <label className={styles.label}>
                    How would you describe your content? <span className={styles.req}>*</span>
                  </label>
                  <textarea
                    className={`${styles.textarea} ${errs.contentDescription ? styles.err : ''}`}
                    value={contentDescription}
                    onChange={(e) => setContentDescription(e.target.value)}
                    placeholder="Tell us about the type of content you make…"
                  />
                  {errs.contentDescription && (
                    <div className={styles.errInline}>{errs.contentDescription}</div>
                  )}
                </div>
                <div className={`${styles.fitem} ${styles.fitemFull}`}>
                  <label className={styles.label}>
                    How would you describe your audience? <span className={styles.req}>*</span>
                  </label>
                  <textarea
                    className={`${styles.textarea} ${errs.audienceDescription ? styles.err : ''}`}
                    value={audienceDescription}
                    onChange={(e) => setAudienceDescription(e.target.value)}
                    placeholder="Age range, interests, what they come to you for…"
                  />
                  {errs.audienceDescription && (
                    <div className={styles.errInline}>{errs.audienceDescription}</div>
                  )}
                </div>
                <div className={`${styles.fitem} ${styles.fitemFull}`}>
                  <label className={styles.label}>
                    What product would you be interested in creating content for?{' '}
                    <span className={styles.req}>*</span>
                  </label>
                  <div className={styles.productChecks}>
                    <label
                      className={`${styles.checkItem} ${productBeta ? styles.checked : ''} ${errs.product ? styles.checkErr : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={productBeta}
                        onChange={(e) => setProductBeta(e.target.checked)}
                      />
                      <span>
                        Beta Blockers{' '}
                        <span style={{ color: '#94a3b8', fontWeight: 400 }}>
                          (situational anxiety)
                        </span>
                      </span>
                    </label>
                    <label
                      className={`${styles.checkItem} ${productSleep ? styles.checked : ''} ${errs.product ? styles.checkErr : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={productSleep}
                        onChange={(e) => setProductSleep(e.target.checked)}
                      />
                      <span>Prescription Sleep Plan</span>
                    </label>
                  </div>
                  {errs.product && <div className={styles.errInline}>{errs.product}</div>}
                </div>
                <div className={`${styles.fitem} ${styles.fitemFull}`}>
                  <label className={styles.label}>
                    Do you have ideas for your video or will you need some help with concepts?{' '}
                    <span className={styles.req}>*</span>
                  </label>
                  <div className={styles.ynToggle}>
                    <button
                      type="button"
                      className={`${styles.ynBtn} ${hasIdeas === 'yes' ? styles.activeYes : ''} ${errs.hasIdeas ? styles.ynErr : ''}`}
                      onClick={() => setHasIdeas('yes')}
                    >
                      Yes, I have ideas
                    </button>
                    <button
                      type="button"
                      className={`${styles.ynBtn} ${hasIdeas === 'no' ? styles.activeNo : ''} ${errs.hasIdeas ? styles.ynErr : ''}`}
                      onClick={() => setHasIdeas('no')}
                    >
                      No, I'll need help
                    </button>
                  </div>
                  {errs.hasIdeas && <div className={styles.errInline}>{errs.hasIdeas}</div>}
                  {hasIdeas === 'yes' && (
                    <div className={styles.conditional}>
                      <label
                        className={styles.label}
                        style={{ display: 'block', marginBottom: 8 }}
                      >
                        If yes to the above, tell us a little more about your video idea.{' '}
                        <span className={styles.req}>*</span>
                      </label>
                      <div className={styles.ideaExamples}>
                        <div className={styles.ideaExamplesLabel}>
                          Need inspiration? Here are some angles creators have used:
                        </div>
                        <ul>
                          <li>A personal story about dealing with situational anxiety</li>
                          <li>
                            A presentation or performance situation where you wish you'd known
                            about beta blockers
                          </li>
                          <li>A specific situation where beta blockers helped you</li>
                          <li>Your sleep struggles and journey toward better sleep</li>
                          <li>Your overall experience with Kick Health</li>
                        </ul>
                      </div>
                      <textarea
                        className={`${styles.textarea} ${errs.ideaDetail ? styles.err : ''}`}
                        value={ideaDetail}
                        onChange={(e) => setIdeaDetail(e.target.value)}
                        placeholder="Tell us your concept in your own words…"
                      />
                      {errs.ideaDetail && (
                        <div className={styles.errInline}>{errs.ideaDetail}</div>
                      )}
                    </div>
                  )}
                  {hasIdeas === 'no' && (
                    <div className={styles.conditional}>
                      <label
                        className={styles.label}
                        style={{ display: 'block', marginBottom: 8 }}
                      >
                        What can we help you with? <span className={styles.req}>*</span>
                      </label>
                      <textarea
                        className={`${styles.textarea} ${errs.helpDetail ? styles.err : ''}`}
                        value={helpDetail}
                        onChange={(e) => setHelpDetail(e.target.value)}
                        placeholder="Tell us what kind of concept help you're looking for…"
                      />
                      {errs.helpDetail && (
                        <div className={styles.errInline}>{errs.helpDetail}</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className={styles.sectionCard}>
              <div className={styles.sectionTitle}>Partnership Agreement</div>
              <div className={styles.checkList}>
                <label
                  className={`${styles.checkItem} ${agreedRate ? styles.checked : ''} ${errs.rate ? styles.checkErr : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={agreedRate}
                    onChange={(e) => setAgreedRate(e.target.checked)}
                  />
                  <span>
                    I accept the $300 flat rate for this partnership, paid upon delivery and
                    approval of the final asset.
                  </span>
                </label>
                <label
                  className={`${styles.checkItem} ${agreedRevisions ? styles.checked : ''} ${errs.revisions ? styles.checkErr : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={agreedRevisions}
                    onChange={(e) => setAgreedRevisions(e.target.checked)}
                  />
                  <span>
                    Our team reviews all content before it goes live and may request revisions to
                    stay compliant. Are you okay working within this process?
                  </span>
                </label>
                <label
                  className={`${styles.checkItem} ${agreedIntake ? styles.checked : ''} ${errs.intake ? styles.checkErr : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={agreedIntake}
                    onChange={(e) => setAgreedIntake(e.target.checked)}
                  />
                  <span>
                    I understand I'll need to complete a ~15 minute online intake with Kick Health
                    to get started. If our medical provider determines the treatment isn't the
                    right fit, we won't be able to move forward with the partnership.
                  </span>
                </label>
              </div>
            </div>

            <div className={styles.sectionCard}>
              <div className={styles.sectionTitle}>Payment Details</div>
              <p
                style={{
                  fontSize: 13,
                  color: '#64748b',
                  lineHeight: 1.55,
                  margin: '-8px 0 18px',
                }}
              >
                Payment is sent via PayPal once your content is approved and delivered.
              </p>
              <div className={styles.fgrid}>
                <div className={`${styles.fitem} ${styles.fitemFull}`}>
                  <label className={styles.label}>
                    PayPal email <span className={styles.req}>*</span>
                  </label>
                  <input
                    type="email"
                    className={`${styles.input} ${errs.paypalEmail ? styles.err : ''}`}
                    value={paypalEmail}
                    onChange={(e) => setPaypalEmail(e.target.value)}
                    placeholder="payouts@example.com"
                    autoComplete="email"
                  />
                  {errs.paypalEmail && (
                    <div className={styles.errInline}>{errs.paypalEmail}</div>
                  )}
                </div>
                <div className={styles.fitem}>
                  <label className={styles.label}>PayPal recipient name</label>
                  <input
                    type="text"
                    className={`${styles.input} ${errs.paypalRecipientName ? styles.err : ''}`}
                    value={paypalRecipientName}
                    onChange={(e) => setPaypalRecipientName(e.target.value)}
                    placeholder="Name on PayPal account"
                    autoComplete="name"
                  />
                  {errs.paypalRecipientName && (
                    <div className={styles.errInline}>{errs.paypalRecipientName}</div>
                  )}
                </div>
                <div className={styles.fitem}>
                  <label className={styles.label}>PayPal phone (optional)</label>
                  <input
                    type="tel"
                    className={`${styles.input} ${errs.paypalPhone ? styles.err : ''}`}
                    value={paypalPhone}
                    onChange={(e) => setPaypalPhone(e.target.value)}
                    placeholder="If your PayPal is registered by phone"
                    autoComplete="tel"
                  />
                  {errs.paypalPhone && (
                    <div className={styles.errInline}>{errs.paypalPhone}</div>
                  )}
                </div>
              </div>
            </div>

            <div className={styles.sectionCard}>
              <div className={styles.sectionTitle}>Anything Else</div>
              <div className={styles.fitem}>
                <label className={styles.label}>
                  Let us know if you have any questions or anything else you would like us to know.
                </label>
                <textarea
                  className={`${styles.textarea} ${errs.extraNotes ? styles.err : ''}`}
                  value={extraNotes}
                  onChange={(e) => setExtraNotes(e.target.value)}
                  placeholder="Optional…"
                />
                {errs.extraNotes && <div className={styles.errInline}>{errs.extraNotes}</div>}
              </div>
            </div>

            <div className={styles.submitArea}>
              {errMsg && <div className={styles.errorBanner}>{errMsg}</div>}
              <button type="submit" className={styles.submitBtn} disabled={submitting}>
                {submitting ? 'Submitting…' : 'Submit Application'}
              </button>
              <p className={styles.privacyNote}>
                Your information is kept private and only used for partnership consideration.
              </p>
            </div>
          </form>
        )}
      </div>
      <footer className={styles.footer}>© 2026 Kick — All rights reserved</footer>
    </div>
  );
}
